import type { World } from '../ecs/world'

/**
 * Broadcast-telemetry HUD: DOM overlay styled as an institutional signal
 * feed (vitals readout, compliance framing), not a generic game UI. Reads
 * players()[0] in v1; the layout leaves room for a second participant column.
 */
export class Hud {
  private root: HTMLElement
  private vitalsFill!: HTMLElement
  private vitalsNum!: HTMLElement
  private lives!: HTMLElement
  private score!: HTMLElement
  private magic!: HTMLElement
  private keys!: HTMLElement
  private armament!: HTMLElement
  private override!: HTMLElement
  private floor!: HTMLElement
  private bossWrap!: HTMLElement
  private bossFill!: HTMLElement
  private bossName!: HTMLElement
  private starving = false

  constructor() {
    this.root = document.getElementById('hud')!
    this.root.innerHTML = `
      <div class="hud-col hud-left">
        <div class="hud-label">PARTICIPANT VITALS</div>
        <div class="vitals-bar"><div class="vitals-fill"></div></div>
        <div class="vitals-num">100</div>
        <div class="hud-row"><span class="hud-label">CONTINUITY</span><span class="lives"></span></div>
        <div class="hud-row"><span class="hud-label">SANCTIONED CHARGES</span><span class="magic"></span></div>
        <div class="hud-row"><span class="hud-label">ACCESS TOKENS</span><span class="keys"></span></div>
        <div class="hud-row"><span class="hud-label">ARMAMENT</span><span class="armament"></span></div>
        <div class="hud-row"><span class="hud-label">OVERRIDE</span><span class="override"></span></div>
      </div>
      <div class="hud-col hud-right">
        <div class="hud-label">PERFORMANCE INDEX</div>
        <div class="score">0</div>
        <div class="floor"></div>
      </div>
      <div class="boss-wrap hidden">
        <div class="boss-name"></div>
        <div class="boss-bar"><div class="boss-fill"></div></div>
      </div>`
    this.vitalsFill = this.root.querySelector('.vitals-fill')!
    this.vitalsNum = this.root.querySelector('.vitals-num')!
    this.lives = this.root.querySelector('.lives')!
    this.score = this.root.querySelector('.score')!
    this.magic = this.root.querySelector('.magic')!
    this.keys = this.root.querySelector('.keys')!
    this.armament = this.root.querySelector('.armament')!
    this.override = this.root.querySelector('.override')!
    this.floor = this.root.querySelector('.floor')!
    this.bossWrap = this.root.querySelector('.boss-wrap')!
    this.bossFill = this.root.querySelector('.boss-fill')!
    this.bossName = this.root.querySelector('.boss-name')!
  }

  show(on: boolean): void {
    this.root.classList.toggle('hidden', !on)
    if (!on && this.starving) {
      // the low-vitals treatment must never outlive the run (menus, end screens)
      this.starving = false
      this.vitalsNum.classList.remove('blink')
      document.body.classList.remove('starving')
    }
  }

  update(world: World, lives: number, override?: { meter: number; active: boolean; cooling: boolean }): void {
    const p = world.players()[0]
    const hp = p?.hp ?? 0
    const maxHp = p?.maxHp ?? 100
    const frac = Math.max(0, hp / maxHp)
    this.vitalsFill.style.width = `${frac * 100}%`
    this.vitalsFill.classList.toggle('low', frac < 0.28)
    const shield = Math.floor(p?.shield ?? 0)
    this.vitalsNum.textContent = String(Math.ceil(hp)) + (shield > 0 ? ` +${shield}` : '')
    const starvingNow = frac < 0.28
    if (starvingNow !== this.starving) {
      this.starving = starvingNow
      this.vitalsNum.classList.toggle('blink', starvingNow)
      // drives the #vignette overlay (styles.css); CSS handles reduced motion
      document.body.classList.toggle('starving', starvingNow)
    }
    this.lives.textContent = '▮'.repeat(Math.max(0, lives)) || '—'
    this.magic.textContent = '◆'.repeat(p?.player?.magic ?? 0) || '—'
    this.keys.textContent = '⚿'.repeat(p?.player?.keys ?? 0) || '—'
    const arm = p?.player?.armament
    this.armament.textContent = arm ? `${arm.id.toUpperCase()} ×${arm.charge}` : 'STANDARD ISSUE'
    if (override) {
      const cells = 8
      const filled = Math.round(override.meter * cells)
      this.override.textContent = override.active
        ? 'ENGAGED'
        : override.cooling
          ? 'COOLING'
          : override.meter >= 1
            ? 'READY — SHIFT'
            : '▮'.repeat(filled) + '░'.repeat(cells - filled)
    }
    this.score.textContent = String(world.score).padStart(7, '0')
    const gensLeft = world.generators().length
    const gate = world.level.exit.type === 'generators' && gensLeft > 0 ? ` · ASSETS ${gensLeft}` : ''
    this.floor.textContent = `FLOOR ${String(world.floor).padStart(2, '0')} · ACT ${world.level.act} — ${world.level.name}${gate}`

    // boss bar (the only health bar in the game)
    const boss = world.entities.find((e) => e.kind === 'boss' && !e.dead)
    if (boss && boss.hp !== undefined && boss.maxHp !== undefined) {
      this.bossWrap.classList.remove('hidden')
      this.bossName.textContent = `FEATURED ENCOUNTER — ${boss.boss?.def.name ?? ''}`
      this.bossFill.style.width = `${Math.max(0, (boss.hp / boss.maxHp) * 100)}%`
    } else {
      this.bossWrap.classList.add('hidden')
    }
  }
}
